import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  useAssignByDepartment,
  useAssignUser,
  useDepartments,
  useDirectoryUsers,
  useRoleUsers,
  useUnassignUser,
} from '@/admin/useAdminRoles';
import { RevokeSessionsButton } from '@/components/admin/RevokeSessionsButton';

export interface RoleUsersPanelProps {
  roleId: number;
}

export function RoleUsersPanel({ roleId }: RoleUsersPanelProps) {
  const { t } = useTranslation();
  const assigned = useRoleUsers(roleId);
  const assign = useAssignUser(roleId);
  const unassign = useUnassignUser(roleId);
  const assignDept = useAssignByDepartment(roleId);
  const departments = useDepartments();

  const [search, setSearch] = useState('');
  const [dept, setDept] = useState('');
  const directory = useDirectoryUsers(search.trim(), dept || null);

  // Los ya asignados se identifican por su object id de Entra.
  const assignedOids = useMemo(
    () => new Set((assigned.data ?? []).map((u) => u.entra_object_id)),
    [assigned.data],
  );

  // Candidatos: TODO el directorio de Entra que aún no tiene el rol.
  const candidates = (directory.data ?? []).filter(
    (u) => !assignedOids.has(u.entra_object_id),
  );

  // Solo departamentos con nombre (el bucket "sin departamento" no se filtra).
  const deptOptions = (departments.data ?? []).filter((d) => d.department);

  return (
    <div className="space-y-3 rounded-md border bg-white p-3">
      <h4 className="text-sm font-semibold text-appsol-900">{t('roles.users.title')}</h4>

      {assigned.isLoading && <p className="text-xs text-slate-500">{t('home.loading')}</p>}
      {assigned.isError && (
        <p role="alert" className="text-xs text-rose-700">
          {t('roles.users.error')}
        </p>
      )}

      {assigned.data && assigned.data.length === 0 && (
        <p className="text-xs italic text-slate-500">{t('roles.users.empty')}</p>
      )}

      {assigned.data && assigned.data.length > 0 && (
        <ul className="space-y-1">
          {assigned.data.map((u) => (
            <li key={u.id} className="flex items-center justify-between gap-2 text-sm">
              <span>
                {u.display_name}
                {u.department && (
                  <span className="ml-1 rounded bg-slate-100 px-1 text-xs text-slate-600">
                    {u.department}
                  </span>
                )}
                {u.email && <span className="ml-1 text-xs text-slate-500">· {u.email}</span>}
              </span>
              <span className="flex shrink-0 items-center gap-3">
                <RevokeSessionsButton userId={u.id} name={u.display_name} />
                <button
                  type="button"
                  aria-label={t('roles.users.remove', { name: u.display_name })}
                  onClick={() => unassign.mutate(u.id)}
                  disabled={unassign.isPending}
                  className="text-xs text-rose-700 hover:underline disabled:opacity-50"
                >
                  {t('roles.users.removeCta')}
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}

      <div className="space-y-2 border-t pt-2">
        <label className="block text-xs font-medium text-slate-600" htmlFor={`search-${roleId}`}>
          {t('roles.users.addLabel')}
        </label>

        <div className="flex flex-wrap items-center gap-2">
          <label className="sr-only" htmlFor={`dept-${roleId}`}>
            {t('roles.users.filterByDepartment')}
          </label>
          <select
            id={`dept-${roleId}`}
            value={dept}
            onChange={(e) => setDept(e.target.value)}
            className="rounded border px-2 py-1 text-sm"
          >
            <option value="">{t('roles.users.allDepartments')}</option>
            {deptOptions.map((d) => (
              <option key={d.department} value={d.department ?? ''}>
                {d.department} ({d.user_count})
              </option>
            ))}
          </select>

          {dept && (
            <button
              type="button"
              onClick={() => assignDept.mutate(dept)}
              disabled={assignDept.isPending}
              className="rounded bg-appsol-900 px-2 py-1 text-xs font-medium text-white hover:bg-appsol-800 disabled:opacity-50"
            >
              {t('roles.users.assignDepartmentCta', { department: dept })}
            </button>
          )}
        </div>

        {assignDept.isSuccess && assignDept.data && (
          <p className="text-xs text-emerald-700">
            {t('roles.users.assignDepartmentResult', {
              assigned: assignDept.data.assigned,
              total: assignDept.data.total,
            })}
          </p>
        )}

        <input
          id={`search-${roleId}`}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('roles.users.searchPlaceholder')}
          className="w-full rounded border px-2 py-1 text-sm"
        />

        {directory.isLoading && <p className="text-xs text-slate-500">{t('home.loading')}</p>}
        {directory.isError && (
          <p role="alert" className="text-xs text-rose-700">
            {t('roles.users.directoryError')}
          </p>
        )}
        {candidates.length > 0 && (
          <ul className="max-h-40 space-y-1 overflow-y-auto">
            {candidates.map((u) => (
              <li
                key={u.entra_object_id}
                className="flex items-center justify-between gap-2 text-sm"
              >
                <span>
                  {u.display_name}
                  {u.department && (
                    <span className="ml-1 rounded bg-slate-100 px-1 text-xs text-slate-600">
                      {u.department}
                    </span>
                  )}
                  {!u.has_logged_in && (
                    <span
                      title={t('roles.users.neverLoggedInHint')}
                      className="ml-1 rounded bg-amber-50 px-1 text-xs text-amber-700"
                    >
                      {t('roles.users.neverLoggedIn')}
                    </span>
                  )}
                  {u.email && <span className="ml-1 text-xs text-slate-500">· {u.email}</span>}
                </span>
                <span className="flex shrink-0 items-center gap-3">
                  {/* Solo quien ya entró puede tener sesiones abiertas. */}
                  {u.has_logged_in && u.local_user_id !== null && (
                    <RevokeSessionsButton userId={u.local_user_id} name={u.display_name} />
                  )}
                  <button
                    type="button"
                    onClick={() => assign.mutate({ entra_object_id: u.entra_object_id })}
                    disabled={assign.isPending}
                    className="text-xs text-appsol-900 hover:underline disabled:opacity-50"
                  >
                    {t('roles.users.addCta')}
                  </button>
                </span>
              </li>
            ))}
          </ul>
        )}
        {directory.data && candidates.length === 0 && !directory.isLoading && (
          <p className="text-xs italic text-slate-500">{t('roles.users.noCandidates')}</p>
        )}
      </div>
    </div>
  );
}
